<script setup>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useLoading } from 'vue-loading-overlay'
import { useToast } from 'vue-toast-notification'
import { useMixpanel } from '@/composables/mixpanel'
import { inject } from 'vue'
import Popup from './Popup.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import EmailField from '@/components/EmailField.vue'
import ButtonComponent from '@/components/ButtonComponent.vue'
import ErrorAlert from '@/components/ErrorAlert.vue'

const props = defineProps({
  open: Boolean,
})

const emit = defineEmits(['update:open', 'completed'])

const $loading = useLoading({
  canCancel: false,
  isFullPage: true,
  color: '#a15cff',
})

const $store = useStore()
const $axios = inject('axios')
const $toast = useToast()
const mixpanel = useMixpanel()

const email = ref('')
const password = ref('')
const error = ref(null)

async function upgradeAnonymousAccount() {
  try {
    const { data } = await $axios({
      method: 'post',
      url: '/auth/update-credentials',
      data: {
        email: email.value.trim(),
        password: password.value,
      },
    })

    const { email: newEmail, emailUnsanitized } = data
    await $store.commit('SET_USER', {
      ...$store.state.user,
      email: newEmail,
      emailUnsanitized,
      anonymousUser: false,
    })
    $store.commit('SET_EMAIL', null)
    $store.commit('SET_PASSWORD', null)
    $store.commit('SET_NAME_FUNNEL', null)
    $store.commit('SET_TEMP_GENDER', null)
  } catch (e) {
    throw e
  }
}

async function handleFormSubmit() {
  if (!email.value) {
    $toast.error('Please enter your email')
    return
  }

  if (!password.value) {
    $toast.error('Please enter a password')
    return
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
    $toast.error('Please enter a valid email')
    return
  }

  const loader = $loading.show()

  try {
    await upgradeAnonymousAccount()

    emit('update:open', false)
    emit('completed')

    $toast.success('Credentials updated successfully')
  } catch (e) {
    const message = e?.response?.data?.message
    console.log(e)
    $toast.error(message || 'An error occurred')
    throw e
  } finally {
    loader.hide()
  }
}
</script>

<template>
  <Popup
    class="sticky"
    :open="open"
    :mobile="false"
    popupStyle="bg-[#0A0D22] w-[625px] pt-[35px] pb-5 px-4 mx-4"
    @update:open="emit('update:open', $event)"
  >
    <div>
      <div class="flex flex-col items-center gap-[25px] text-center">
        <SvgIcon
          sprite="home"
          icon-id="logo"
          class="w-[130px] lg:w-[153px] aspect-[865/120]"
          alt="Logo"
        />

        <div>
          <h2 class="text-[30px] lg:text-[35px] leading-[1] mb-3">
            Save Your Chats
          </h2>
          <p class="text-[#B1B5DB] text-base max-w-md">
            Sign up to keep your AI chats perfectly in sync across all your
            devices.
          </p>
        </div>
      </div>

      <div class="lg:px-6 mt-8">
        <ErrorAlert v-if="error" :message="error" class="mb-4" />

        <label
          for="email"
          class="text-white text-opacity-80 text-sm lg:text-base leading-[1]"
          >Email Address</label
        >
        <div class="space-y-6">
          <div class="space-y-4">
            <EmailField
              :show-label="false"
              placeholder="your@email.com"
              :email="email"
              @update:email="email = $event"
            />

            <div>
              <label
                for="password"
                autocomplete="new-password"
                class="text-white text-opacity-80 text-sm lg:text-base leading-[1]"
                >Password</label
              >
              <div class="mt-[5px] lg:mt-[10px]">
                <input
                  id="password"
                  v-model="password"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  required=""
                  class="block w-full h-[50px] pl-[25px] bg-[#0E122A] py-[18px] text-white rounded-[15px] border border-[#111631]"
                />
              </div>
            </div>
          </div>

          <ButtonComponent
            class="w-full py-[14px] rounded-[15px]"
            @click="handleFormSubmit"
            :disabled="!email || !password"
          >
            Continue Chatting
          </ButtonComponent>
        </div>
      </div>
    </div>
  </Popup>
</template>
